import { useEffect, useMemo, useState } from 'react';
import {
  PRODUCT_TYPES,
  PRODUCT_TYPE_LABELS,
  type Product,
  type ProductType,
} from '@sirajoptic/shared';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { InputField, SelectField, TextareaField } from '@/components/ui/field';
import { Encart } from '@/components/ui/feedback';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api-client';
import { useCreerProduit, useModifierProduit } from '../api/stock.api';

/**
 * Les champs d'attributs suivent le type sélectionné : c'est le pendant de
 * l'union discriminée partagée. Ajouter un quatrième type de produit se limite
 * à un membre d'union et une entrée dans cette table.
 */
const ATTRIBUTS: Record<ProductType, { cle: string; label: string }[]> = {
  MONTURE: [
    { cle: 'reference', label: 'Référence' },
    { cle: 'couleur', label: 'Couleur' },
  ],
  LENTILLE: [
    { cle: 'correction', label: 'Correction' },
    { cle: 'prod_lentille', label: 'Produit / marque' },
  ],
  VERRE: [
    { cle: 'sph', label: 'SPH' },
    { cle: 'cyl', label: 'CYL' },
  ],
};

type Formulaire = {
  type: ProductType;
  nom: string;
  description: string;
  quantite: string;
  prix_initial: string;
  prix_vente: string;
  attributs: Record<string, string>;
};

const VIDE: Formulaire = {
  type: 'MONTURE',
  nom: '',
  description: '',
  quantite: '0',
  prix_initial: '',
  prix_vente: '',
  attributs: {},
};

export function ProduitDialog({
  ouvert,
  produit,
  onFermer,
}: {
  ouvert: boolean;
  /** null => création. */
  produit: Product | null;
  onFermer: () => void;
}) {
  const [form, setForm] = useState<Formulaire>(VIDE);
  /** État d'ouverture, pour repérer une saisie non enregistrée. */
  const [initial, setInitial] = useState<Formulaire>(VIDE);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const creer = useCreerProduit();
  const modifier = useModifierProduit();
  const toast = useToast();

  useEffect(() => {
    if (!ouvert) return;
    setErreurs({});
    const depart: Formulaire =
      produit
        ? {
            type: produit.type,
            nom: produit.nom,
            description: produit.description ?? '',
            quantite: String(produit.quantite),
            prix_initial: String(produit.prix_initial),
            prix_vente: String(produit.prix_vente),
            attributs: { ...produit.attributs },
          }
        : VIDE;
    setForm(depart);
    setInitial(depart);
  }, [ouvert, produit]);

  const modifie = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initial),
    [form, initial],
  );

  function set<K extends keyof Formulaire>(cle: K, valeur: Formulaire[K]) {
    setForm((f) => ({ ...f, [cle]: valeur }));
  }

  function valider(): boolean {
    const e: Record<string, string> = {};
    if (!form.nom.trim()) e.nom = 'Le nom est obligatoire.';
    if (form.prix_initial === '' || Number.isNaN(Number(form.prix_initial)))
      e.prix_initial = "Prix d'achat invalide.";
    if (form.prix_vente === '' || Number.isNaN(Number(form.prix_vente)))
      e.prix_vente = 'Prix de vente invalide.';
    if (!Number.isInteger(Number(form.quantite)) || Number(form.quantite) < 0)
      e.quantite = 'Quantité invalide.';
    setErreurs(e);
    return Object.keys(e).length === 0;
  }

  async function soumettre() {
    if (!valider()) return;

    const attributs = Object.fromEntries(
      ATTRIBUTS[form.type].map((a) => [a.cle, form.attributs[a.cle] ?? '']),
    );

    try {
      if (produit) {
        await modifier.mutateAsync({
          id: produit.id,
          patch: {
            nom: form.nom.trim(),
            description: form.description.trim() || null,
            prix_initial: Number(form.prix_initial),
            prix_vente: Number(form.prix_vente),
            quantite: Number(form.quantite),
            attributs,
            note: 'Correction depuis la fiche produit',
          },
        });
        toast.succes('Produit mis à jour.');
      } else {
        await creer.mutateAsync({
          type: form.type,
          nom: form.nom.trim(),
          description: form.description.trim() || null,
          quantite: Number(form.quantite),
          prix_initial: Number(form.prix_initial),
          prix_vente: Number(form.prix_vente),
          attributs,
        } as Parameters<typeof creer.mutateAsync>[0]);
        toast.succes('Produit ajouté au stock.');
      }
      onFermer();
    } catch (e) {
      if (e instanceof ApiError) {
        setErreurs(e.details ?? { nom: e.message });
        toast.erreur(e.message);
      } else {
        toast.erreur("Le produit n'a pas pu être enregistré.");
      }
    }
  }

  const enCours = creer.isPending || modifier.isPending;

  return (
    <Dialog
      ouvert={ouvert}
      onFermer={onFermer}
      titre={produit ? 'Modifier le produit' : 'Nouveau produit'}
      modifie={modifie && !enCours}
      description={produit ? produit.nom : 'Les champs s’adaptent au type choisi.'}
      pied={
        <>
          <Button variant="outline" onClick={onFermer} disabled={enCours}>
            Annuler
          </Button>
          <Button variant="primary" onClick={() => void soumettre()} chargement={enCours}>
            {produit ? 'Enregistrer' : 'Ajouter au stock'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SelectField
            label="Type"
            value={form.type}
            // Le type est figé après création : il détermine la forme des attributs.
            disabled={Boolean(produit)}
            onChange={(e) => {
              set('type', e.target.value as ProductType);
              set('attributs', {});
            }}
            aide={produit ? 'Le type ne se modifie pas après création.' : undefined}
            requis
          >
            {PRODUCT_TYPES.map((t) => (
              <option key={t} value={t}>
                {PRODUCT_TYPE_LABELS[t]}
              </option>
            ))}
          </SelectField>

          <InputField
            label="Nom"
            value={form.nom}
            onChange={(e) => set('nom', e.target.value)}
            erreur={erreurs.nom}
            requis
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <InputField
            label="Prix d'achat (DH)"
            type="number"
            step="0.01"
            value={form.prix_initial}
            onChange={(e) => set('prix_initial', e.target.value)}
            erreur={erreurs.prix_initial}
            requis
          />
          <InputField
            label="Prix de vente (DH)"
            type="number"
            step="0.01"
            value={form.prix_vente}
            onChange={(e) => set('prix_vente', e.target.value)}
            erreur={erreurs.prix_vente}
            requis
          />
          <InputField
            label="Quantité"
            type="number"
            step="1"
            min="0"
            value={form.quantite}
            onChange={(e) => set('quantite', e.target.value)}
            erreur={erreurs.quantite}
            aide={produit ? 'Toute correction crée un mouvement.' : undefined}
            requis
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {ATTRIBUTS[form.type].map((a) => (
            <InputField
              key={a.cle}
              label={a.label}
              value={form.attributs[a.cle] ?? ''}
              onChange={(e) => set('attributs', { ...form.attributs, [a.cle]: e.target.value })}
            />
          ))}
        </div>

        <TextareaField
          label="Description"
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          rows={2}
        />

        {produit ? (
          <Encart ton="moutarde">
            Modifier la quantité ici enregistre un mouvement « Correction » dans l'historique.
          </Encart>
        ) : null}
      </div>
    </Dialog>
  );
}
